import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import CustomerCreditsFormModal from './CustomerCreditsFormModal';
import CustomerService from 'services/CustomerService';
import { FontAwesomeIconStatus } from 'components/icon/FontAwesomeIconStatus';
import AuthService from 'services/AuthService';
import NotAuthorized from 'views/NotAuthorized';
import CustomerCreditService from 'services/CustomerCreditService';
import ButtonStatus from 'components/buttons/ButtonStatus';
import { CREDITS_MOVEMENT_TYPE_ADD, CREDITS_MOVEMENT_TYPE_REMOVE } from './customerCreditsHelpers';
import NumberUtils from 'commons/utils/numberUtils';
import LabelStatus from 'components/labels/LabelStatus';
import HintText from 'components/labels/HintText';
import moment from 'moment';
import FontAwesomeDoubleIconStatus from 'components/icon/FontAwesomeDoubleIconStatus';
import { currentUserSelector, useAppStore } from 'stores/appStore';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  spacer: {
    flexGrow: 1,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  topBtnIcon: {
    marginRight: theme.spacing(1),
  },
  topBtnAdd: {
    marginLeft: theme.spacing(1),
  },
  topLabel: {
    marginRight: theme.spacing(5),
  },
  selectableRow: {
    cursor: 'pointer',
  },
}));

function checkCanEditDeleteAppointment(myUserRoles) {
  const cannotEditAppointments = AuthService.checkCannotEditAppointments(myUserRoles);
  if (cannotEditAppointments) {
    return false;
  }

  const cannotDeleteAppointments = AuthService.checkCannotDeleteAppointments(myUserRoles);
  return !cannotDeleteAppointments;
}

// COMPONENT
function CustomerCreditsListPage() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [summaryResult, setSummaryResult] = useState({
    customerCredits: [],
    totalInCredits: 0,
  });
  const [customer, setCustomer] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [modalSave, setModalSave] = useState({ open: false, object: null });

  const { customerId } = useParams();
  const currentUser = useAppStore(currentUserSelector);

  const hasPermissionForEditing = AuthService.allowsAgenda(currentUser?.roles);
  const cannotAccessCustomer = AuthService.checkCannotAccessCustomer(currentUser?.roles);
  const cannotEditCustomer =
    !AuthService.checkCanDisplayMenuCustomers(currentUser?.roles) &&
    (!hasPermissionForEditing || cannotAccessCustomer || !checkCanEditDeleteAppointment(currentUser?.roles));

  const reloadDataSource = () => {
    if (customer) {
      setIsLoading(true);
      CustomerCreditService.findSummary(customer.id)
        .then((summaryData) => {
          setSummaryResult(summaryData);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    async function initData() {
      if (!cannotEditCustomer) {
        setIsLoading(true);
        try {
          const [customerResp, summaryData] = await Promise.all([
            CustomerService.getById(customerId),
            CustomerCreditService.findSummary(customerId),
          ]);

          setCustomer(customerResp);
          setSummaryResult(summaryData);
        } finally {
          setIsLoading(false);
        }
      }
    }
    initData();
  }, [customerId, navigate, cannotEditCustomer]);

  const handleSelect = (object) => {
    setModalSave({
      open: true,
      object,
    });
  };

  if (cannotEditCustomer) {
    return <NotAuthorized />;
  }

  return (
    <div className={classes.root}>
      {customer && (
        <CustomerCreditsFormModal
          customer={customer}
          open={modalSave.open}
          onClose={() => {
            setModalSave((prev) => ({ ...prev, open: false }));
          }}
          object={modalSave.object}
          onSave={reloadDataSource}
        />
      )}

      <div className={classes.row}>
        <Box display="flex" direction="row" justify="center" alignItems="center">
          <IconButton onClick={() => navigate(-1)} style={{ marginRight: 4 }} size="large">
            <FontAwesomeIconStatus icon="arrow-left" status="primary" />
          </IconButton>
          <Typography variant="overline">Crédito do Cliente: {customer?.name}</Typography>
        </Box>
        <span className={classes.spacer} />

        <Box display="flex" flexDirection="row" alignItems="center">
          <LabelStatus status="primary" variant="h6" className={classes.topLabel}>
            Crédito total: {NumberUtils.toCurrency(summaryResult.totalInCredits)}
          </LabelStatus>
          <ButtonStatus
            status="error"
            variant="outlined"
            onClick={() => {
              setModalSave({
                open: true,
                object: {
                  value: '',
                  customerId: customer.id,
                  movementType: CREDITS_MOVEMENT_TYPE_REMOVE,
                },
              });
            }}
          >
            <FontAwesomeIconStatus icon="minus" status="error" className={classes.topBtnIcon} />
            Crédito
          </ButtonStatus>
          <ButtonStatus
            status="success"
            variant="outlined"
            onClick={() => {
              setModalSave({
                open: true,
                object: {
                  value: '',
                  customerId: customer.id,
                  movementType: CREDITS_MOVEMENT_TYPE_ADD,
                },
              });
            }}
            className={classes.topBtnAdd}
          >
            <FontAwesomeIconStatus icon="plus" status="success" className={classes.topBtnIcon} />
            Crédito
          </ButtonStatus>
        </Box>
      </div>

      {isLoading ? <LinearProgress /> : <div style={{ height: '4px' }} />}

      {summaryResult.customerCredits.length === 0 && <HintText>Nenhum resultado</HintText>}
      {summaryResult.customerCredits.length > 0 && (
        <>
          <HintText className={classes.textTableHint}>Histórico de movimentações de crédito</HintText>
          <Paper variant="outlined" style={{ marginTop: '10px' }}>
            <Table>
              <TableBody>
                {summaryResult.customerCredits.map((row, idx) => {
                  const isAdding = row.movementType === CREDITS_MOVEMENT_TYPE_ADD;
                  return (
                    <React.Fragment key={idx}>
                      <TableRow
                        className={classes.selectableRow}
                        hover
                        onClick={() => {
                          handleSelect(row);
                        }}
                      >
                        <TableCell>
                          <Box display="flex" flexDirection="row" alignItems="center">
                            <FontAwesomeDoubleIconStatus
                              icon1="coins"
                              icon2={isAdding ? 'plus' : 'minus'}
                              status={isAdding ? 'success' : 'error'}
                              fixedWidth
                              size1="lg"
                              size2="sm"
                              style2={{ marginTop: '5px' }}
                            />
                            <Typography style={{ marginLeft: '20px', marginTop: '2px' }}>
                              Crédito {isAdding ? 'adicionado' : 'removido'} de{' '}
                              {NumberUtils.toCurrency(row.value)}
                            </Typography>
                            <Typography variant="subtitle2" style={{ marginLeft: '20px' }}>
                              {row.description}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          Criado por {row.createdBy?.name ?? '-'} às{' '}
                          {moment(row.createdAt).format('DD/MM/YY LT')}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </div>
  );
}

export default CustomerCreditsListPage;
